import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, TrendingDown, DollarSign, AlertCircle, CheckCircle, List, Eye, Plus, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { axiosInstance } from "../App";
import { getErrorMessage } from "../utils/errorHandler";
import { calculateExpectedCash } from "../utils/cashCalculations";
import { todayCR } from "../utils/dateHelpers";

export default function LiveReport({ cashRegister, selectedBox, pendingVentas, onTransactionCreated }) {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedTypeForCreate, setSelectedTypeForCreate] = useState(null);
  const [selectedBank, setSelectedBank] = useState(""); // Changed to empty, will be set from banks list
  const [bankMovementType, setBankMovementType] = useState("entrada"); // "entrada" or "salida"
  const [creating, setCreating] = useState(false);
  const [vaults, setVaults] = useState([]);  // NEW: List of vaults/banks
  const [showNewVaultInput, setShowNewVaultInput] = useState(false);  // NEW: Show input for new vault
  const [newVaultName, setNewVaultName] = useState("");  // NEW: Name of new vault
  const [banks, setBanks] = useState([]);  // Lista de bancos/bóvedas
  // State for debt totals from database
  const [debtTotals, setDebtTotals] = useState({
    por_cobrar: { colones: 0, dollars: 0, count: 0 },
    por_pagar: { colones: 0, dollars: 0, count: 0 }
  });
  // Form data for creating transactions
  const [formData, setFormData] = useState({
    amount_colones: 0,
    amount_dollars: 0,
    description: "",
    customer_name: "",
    vault_name: ""  // NEW: For SINPE - banco/bóveda destination
  });

  // Fetch vaults for this box
  useEffect(() => {
    if (!cashRegister) return;
    
    const fetchVaults = async () => {
      try {
        const boxId = cashRegister?.box_id;
        const url = boxId ? `/banks?box_id=${boxId}` : '/banks';
        const response = await axiosInstance.get(url);
        setVaults(response.data);
      } catch (error) {
        console.error("Error fetching banks:", error);
      }
    };
    
    fetchVaults();
  }, [cashRegister]);

  // Fetch debt totals for TODAY (current cash register session)
  useEffect(() => {
    if (!cashRegister || !cashRegister.id) return;
    
    const fetchDebtTotals = async () => {
      try {
        const response = await axiosInstance.get('/debts/summary/totals-today', {
          params: { cash_register_id: cashRegister.id }
        });
        setDebtTotals(response.data);
      } catch (error) {
        console.error("Error fetching debt totals:", error);
      }
    };
    
    fetchDebtTotals();
    
    // Refresh debt totals every 5 seconds
    const interval = setInterval(fetchDebtTotals, 5000);
    return () => clearInterval(interval);
  }, [cashRegister]);

  // Fetch banks/vaults
  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const boxId = cashRegister?.box_id;
        const url = boxId ? `/banks?box_id=${boxId}` : '/banks';
        const response = await axiosInstance.get(url);
        setBanks(response.data || []);
        // Set default bank if available
        if (response.data && response.data.length > 0) {
          setSelectedBank(response.data[0].id);
        }
      } catch (error) {
        console.error("Error fetching banks:", error);
      }
    };
    fetchBanks();
  }, []);

  if (!cashRegister) return null;

  // Ingresos por datáfono hoy (agregados por banco).
  // Recorre bank.transactions y suma las entradas de hoy con datafono_id presente.
  const todayISO = todayCR(); // YYYY-MM-DD
  const cardIncomeByBank = [];
  let cardIncomeTotal = 0;
  banks.forEach((bank) => {
    const txs = bank.transactions || [];
    let bankTotal = 0;
    const perDatafono = {};
    txs.forEach((t) => {
      if (!t.datafono_id) return;
      const created = (t.created_at || "").slice(0, 10);
      if (created !== todayISO) return;
      const amt = Number(t.amount_colones || 0);
      bankTotal += amt;
      const key = t.datafono_name || t.datafono_id;
      perDatafono[key] = (perDatafono[key] || 0) + amt;
    });
    if (bankTotal > 0) {
      cardIncomeByBank.push({
        bank_id: bank.id,
        bank_name: bank.name,
        total: bankTotal,
        per_datafono: perDatafono,
      });
      cardIncomeTotal += bankTotal;
    }
  });

  const formatCurrency = (amount, currency = "colones") => {
    const symbol = currency === "colones" ? "₡" : "$";
    return `${symbol}${amount.toFixed(2)}`;
  };
  
  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString('es-CR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };
  
  const getTypeLabel = (type) => {
    const labels = {
      venta: "Venta en Efectivo",
      premio: "Premio (Salida)",
      sinpe: "SINPE Móvil",
      entrada: "Entrada de Dinero",
      salida: "Salida de Dinero",
      entrada_banco: "Entrada desde Banco/Bóveda",
      salida_banco: "Salida a Banco/Bóveda",
      // salida_premios: "Salida Premios",  // REMOVED: Duplicates premio
      cuenta_por_cobrar: "Cuentas por Cobrar",
      aumento_cuenta_por_cobrar: "Aumento Cuenta por Cobrar",
      cuenta_por_pagar: "Cuentas por Pagar",
      // pago_efectivo: "Pago en Efectivo",  // REMOVED: Not needed
      pago_con_premio_afecta: "Pago con Premio (Afecta Caja)",
      pago_dias_anteriores_afecta: "Pago Días Anteriores (Afecta)",
      pago_dias_anteriores_no_afecta: "Pago Días Anteriores (NO Afecta)"
    };
    
    if (type?.startsWith("banco_") || type === "pago_banco") {
      return "Pago por Banco";
    }
    
    return labels[type] || type;
  };
  
  const getTransactionsByType = (type) => {
    if (!cashRegister.transactions) return [];
    
    if (type === "bancos") {
      return cashRegister.transactions.filter(t => t.type?.startsWith("banco_") || t.type === "pago_banco" || t.type === "entrada_banco" || t.type === "salida_banco");
    }
    
    // Para cuentas por cobrar, incluir transacciones con debt_operation="pago" y tipo aumento_cuenta_por_cobrar
    if (type === "cuentas_por_cobrar") {
      return cashRegister.transactions.filter(t => 
        t.type === "cuenta_por_cobrar" || 
        t.type === "aumento_cuenta_por_cobrar" ||
        (t.debt_id && t.debt_operation === "pago")
      );
    }
    
    const typeMap = {
      ventas: "venta",
      premios: "premio",
      sinpe: "sinpe",
      entradas: "entrada",
      salidas: "salida",
      // salida_premios: "salida_premios",  // REMOVED
      cuentas_por_pagar: "cuenta_por_pagar",
      // pago_efectivo: "pago_efectivo",  // REMOVED
      pago_dias_anteriores_afecta: "pago_dias_anteriores_afecta",
      pago_dias_anteriores_no_afecta: "pago_dias_anteriores_no_afecta",
      pago_con_premio_afecta: "pago_con_premio_afecta"
    };
    
    return cashRegister.transactions.filter(t => t.type === typeMap[type]);
  };
  
  const handleViewDetails = (type) => {
    setSelectedType(type);
    setDialogOpen(true);
  };
  
  const handleCreateNew = (type) => {
    setSelectedTypeForCreate(type);
    setSelectedBank("banco_payser"); // Default bank
    setFormData({
      amount_colones: 0,
      amount_dollars: 0,
      description: "",
      customer_name: ""
    });
    setCreateDialogOpen(true);
  };
  
  const handleCreateTransaction = async () => {
    if (!cashRegister || !selectedTypeForCreate) {
      toast.error("Error: No hay información de caja");
      return;
    }
    
    if (formData.amount_colones <= 0 && formData.amount_dollars <= 0) {
      toast.error("Ingresa al menos un monto");
      return;
    }
    
    // Special validation for SINPE - must select a bank/vault
    if (selectedTypeForCreate === "sinpe" && !formData.vault_name) {
      toast.error("Selecciona un banco/bóveda de destino para el SINPE");
      return;
    }
    
    // Special validation for cuentas_por_cobrar
    if (selectedTypeForCreate === "cuentas_por_cobrar" && !formData.customer_name) {
      toast.error("Ingresa el nombre del cliente");
      return;
    }
    
    setCreating(true);
    try {
      // For cuentas_por_cobrar, create a debt instead of a transaction
      if (selectedTypeForCreate === "cuentas_por_cobrar") {
        await axiosInstance.post(`/debts?box_id=${cashRegister.box_id}`, {
          customer_name: formData.customer_name,
          amount_colones: parseFloat(formData.amount_colones),
          amount_dollars: parseFloat(formData.amount_dollars),
          description: formData.description || "Cuenta por cobrar",
          type: "por_cobrar"
        });
        
        toast.success("Cuenta por cobrar creada exitosamente");
      } 
      // For cuentas_por_pagar, create a debt
      else if (selectedTypeForCreate === "cuentas_por_pagar") {
        await axiosInstance.post(`/debts?box_id=${cashRegister.box_id}`, {
          customer_name: formData.customer_name || "Proveedor",
          amount_colones: parseFloat(formData.amount_colones),
          amount_dollars: parseFloat(formData.amount_dollars),
          description: formData.description || "Cuenta por pagar",
          type: "por_pagar"
        });
        
        toast.success("Cuenta por pagar creada exitosamente");
      }
      // For bancos, use the existing bank transactions endpoint
      else if (selectedTypeForCreate === "bancos") {
        if (!selectedBank) {
          toast.error("Selecciona un banco/bóveda");
          return;
        }
        
        const bank = banks.find(b => b.id === selectedBank);
        
        // Validate balance for withdrawals
        if (bankMovementType === "salida") {
          const bankBalance = bank?.total_colones || 0;
          if (parseFloat(formData.amount_colones) > bankBalance) {
            toast.error(`Saldo insuficiente en ${bank?.name}. Disponible: ₡${bankBalance.toLocaleString('es-CR', {minimumFractionDigits: 2})}`);
            return;
          }
        }
        
        // Use the existing bank transactions endpoint
        await axiosInstance.post('/banks/transactions', {
          bank_id: selectedBank,
          type: bankMovementType, // "entrada" or "salida"
          amount_colones: parseFloat(formData.amount_colones),
          amount_dollars: parseFloat(formData.amount_dollars),
          description: formData.description || `${bankMovementType === "entrada" ? "Entrada" : "Salida"} de banco`,
          cash_register_id: bankMovementType === "salida" ? cashRegister.id : null // Pass register ID for salidas
        });
        
        // Refresh banks list to show updated balance
        const banksResponse = await axiosInstance.get('/banks');
        setBanks(banksResponse.data || []);
        
        toast.success(`${bankMovementType === "entrada" ? "Entrada" : "Salida"} registrada en ${bank?.name}`);
      }
      // Normal transaction creation
      else {
        // Map group type to transaction type
        const typeMap = {
          ventas: "venta",
          premios: "premio",
          sinpe: "sinpe",
          entradas: "entrada",
          salidas: "salida",
          pago_dias_anteriores_afecta: "pago_dias_anteriores_afecta",
          pago_dias_anteriores_no_afecta: "pago_dias_anteriores_no_afecta"
        };
        
        const transactionType = typeMap[selectedTypeForCreate];
        
        // Prepare transaction data
        const transactionPayload = {
          type: transactionType,
          amount_colones: parseFloat(formData.amount_colones),
          amount_dollars: parseFloat(formData.amount_dollars),
          description: formData.description || `${getTypeLabel(transactionType)} - Ingreso rápido`
        };
        
        // Include vault_name for SINPE transactions
        if (transactionType === "sinpe" && formData.vault_name) {
          transactionPayload.vault_name = formData.vault_name;
        }
        
        await axiosInstance.post(`/transactions?box_id=${cashRegister.box_id}`, transactionPayload);
        
        toast.success("Movimiento registrado exitosamente");
      }
      
      setCreateDialogOpen(false);
      setFormData({
        amount_colones: 0,
        amount_dollars: 0,
        description: "",
        customer_name: "",
        vault_name: ""  // Reset vault_name
      });
      
      // Notify parent to refresh
      if (onTransactionCreated) {
        onTransactionCreated();
      }
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al registrar movimiento"));
    } finally {
      setCreating(false);
    }
  };

  // Calculate expected cash using centralized utility
  const { expectedColones, expectedDollars } = calculateExpectedCash(cashRegister, pendingVentas);
  const diffColones = cashRegister.total_cash_colones - expectedColones;
  const diffDollars = cashRegister.total_cash_dollars - expectedDollars;

  // Group transactions by type
  const groupTransactions = () => {
    const groups = {
      ventas: { count: 0, colones: 0, dollars: 0 },
      premios: { count: 0, colones: 0, dollars: 0 },
      sinpe: { count: 0, colones: 0, dollars: 0 },
      tarjeta_banco: { count: 0, colones: 0, dollars: 0 },
      bancos: { count: 0, colones: 0, dollars: 0 },
      entradas: { count: 0, colones: 0, dollars: 0 },
      salidas: { count: 0, colones: 0, dollars: 0 },
      // salida_premios: { count: 0, colones: 0, dollars: 0 },  // REMOVED
      cuentas_por_cobrar: { count: 0, colones: 0, dollars: 0 },
      cuentas_por_pagar: { count: 0, colones: 0, dollars: 0 },
      // pago_efectivo: { count: 0, colones: 0, dollars: 0 },  // REMOVED
      pago_dias_anteriores_afecta: { count: 0, colones: 0, dollars: 0 },
      pago_dias_anteriores_no_afecta: { count: 0, colones: 0, dollars: 0 },
      pago_con_premio_afecta: { count: 0, colones: 0, dollars: 0 },
      // Ventas de inventario
      ventas_inventario: { count: 0, colones: 0, dollars: 0 },  // Efectivo
      pago_electronico_inventario: { count: 0, colones: 0, dollars: 0, sinpe: 0, tarjeta: 0 },  // SINPE + Tarjeta combinado
      nota_credito_inventario: { count: 0, colones: 0, dollars: 0 }  // Devoluciones
    };

    cashRegister.transactions?.forEach(t => {
      // Skip voided transactions EXCEPT for venta_inventario
      // (venta_inventario voided should still show because nota_credito cancels it)
      if (t.voided && t.type !== "venta_inventario") return;
      
      // Si es aumento de cuenta por cobrar, clasificar ahí
      if (t.type === "aumento_cuenta_por_cobrar") {
        groups.cuentas_por_cobrar.count++;
        groups.cuentas_por_cobrar.colones += t.amount_colones;
        groups.cuentas_por_cobrar.dollars += t.amount_dollars;
      } else if (t.type === "venta") {
        groups.ventas.count++;
        groups.ventas.colones += t.amount_colones;
        groups.ventas.dollars += t.amount_dollars;
      } else if (t.type === "premio") {
        groups.premios.count++;
        groups.premios.colones += t.amount_colones;
        groups.premios.dollars += t.amount_dollars;
      } else if (t.type === "sinpe" && t.channel === "tarjeta") {
        groups.tarjeta_banco.count++;
        groups.tarjeta_banco.colones += t.amount_colones;
        groups.tarjeta_banco.dollars += t.amount_dollars;
      } else if (t.type === "sinpe") {
        groups.sinpe.count++;
        groups.sinpe.colones += t.amount_colones;
        groups.sinpe.dollars += t.amount_dollars;
      } else if (t.type.startsWith("banco_") || t.type === "pago_banco" || t.type === "entrada_banco" || t.type === "salida_banco") {
        groups.bancos.count++;
        groups.bancos.colones += t.amount_colones;
        groups.bancos.dollars += t.amount_dollars;
      } else if (t.type === "entrada") {
        groups.entradas.count++;
        groups.entradas.colones += t.amount_colones;
        groups.entradas.dollars += t.amount_dollars;
      } else if (t.type === "salida") {
        groups.salidas.count++;
        groups.salidas.colones += t.amount_colones;
        groups.salidas.dollars += t.amount_dollars;
      } else if (t.type === "cuenta_por_cobrar") {
        groups.cuentas_por_cobrar.count++;
        groups.cuentas_por_cobrar.colones += t.amount_colones;
        groups.cuentas_por_cobrar.dollars += t.amount_dollars;
      } else if (t.type === "cuenta_por_pagar") {
        groups.cuentas_por_pagar.count++;
        groups.cuentas_por_pagar.colones += t.amount_colones;
        groups.cuentas_por_pagar.dollars += t.amount_dollars;
      } else if (t.type === "pago_dias_anteriores_afecta") {
        groups.pago_dias_anteriores_afecta.count++;
        groups.pago_dias_anteriores_afecta.colones += t.amount_colones;
        groups.pago_dias_anteriores_afecta.dollars += t.amount_dollars;
      } else if (t.type === "pago_dias_anteriores_no_afecta") {
        groups.pago_dias_anteriores_no_afecta.count++;
        groups.pago_dias_anteriores_no_afecta.colones += t.amount_colones;
        groups.pago_dias_anteriores_no_afecta.dollars += t.amount_dollars;
      } else if (t.type === "pago_con_premio_afecta") {
        groups.pago_con_premio_afecta.count++;
        groups.pago_con_premio_afecta.colones += t.amount_colones;
        groups.pago_con_premio_afecta.dollars += t.amount_dollars;
      } else if (t.type === "venta_inventario") {
        // Venta de inventario en efectivo
        groups.ventas_inventario.count++;
        groups.ventas_inventario.colones += t.amount_colones;
        groups.ventas_inventario.dollars += t.amount_dollars;
      } else if (t.type === "pago_electronico_inventario" || t.type === "sinpe_inventario" || t.type === "tarjeta_inventario") {
        // Pago electrónico de inventario (SINPE o Tarjeta)
        groups.pago_electronico_inventario.count++;
        groups.pago_electronico_inventario.colones += t.amount_colones;
        groups.pago_electronico_inventario.dollars += t.amount_dollars;
        // Contar por tipo de pago
        if (t.payment_method === "sinpe") {
          groups.pago_electronico_inventario.sinpe++;
        } else if (t.payment_method === "tarjeta") {
          groups.pago_electronico_inventario.tarjeta++;
        }
      } else if (t.type === "nota_credito_inventario") {
        // Nota de crédito (devolución) de inventario
        groups.nota_credito_inventario.count++;
        groups.nota_credito_inventario.colones += t.amount_colones;
        groups.nota_credito_inventario.dollars += t.amount_dollars;
      }
    });

    // CRITICAL FIX: Override cuentas_por_cobrar and cuentas_por_pagar with actual pending debt amounts from database
    // This ensures that payments correctly reduce the displayed debt totals
    groups.cuentas_por_cobrar = {
      count: debtTotals.por_cobrar.count,
      colones: debtTotals.por_cobrar.colones,
      dollars: debtTotals.por_cobrar.dollars
    };
    
    groups.cuentas_por_pagar = {
      count: debtTotals.por_pagar.count,
      colones: debtTotals.por_pagar.colones,
      dollars: debtTotals.por_pagar.dollars
    };

    return groups;
  };

  const groups = groupTransactions();

  const isMatch = Math.abs(diffColones) < 0.01 && Math.abs(diffDollars) < 0.01;

  return (
    <div className="space-y-3">
      {/* Pending Values Alert */}
      {pendingVentas && ((pendingVentas.venta_macho || 0) > 0 || (pendingVentas.venta_tiro || 0) > 0 || (pendingVentas.premio_macho || 0) > 0 || (pendingVentas.premio_tiro || 0) > 0) && (
        <Card className="border-yellow-300 bg-yellow-50">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base text-yellow-800">
              <AlertCircle className="w-4 h-4" />
              ⚠️ Hay valores pendientes de guardar
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <p className="font-semibold text-yellow-700 mb-1">Ventas Pendientes:</p>
                {(pendingVentas.venta_macho || 0) > 0 && (
                  <p className="text-yellow-800">• VENTA MACHO: ₡{(pendingVentas.venta_macho || 0).toFixed(2)}</p>
                )}
                {(pendingVentas.venta_tiro || 0) > 0 && (
                  <p className="text-yellow-800">• VENTA TIRO: ₡{(pendingVentas.venta_tiro || 0).toFixed(2)}</p>
                )}
                {((pendingVentas.venta_macho || 0) + (pendingVentas.venta_tiro || 0)) > 0 && (
                  <p className="font-semibold text-yellow-900 mt-1">
                    Total: ₡{((pendingVentas.venta_macho || 0) + (pendingVentas.venta_tiro || 0)).toFixed(2)}
                  </p>
                )}
              </div>
              <div>
                <p className="font-semibold text-yellow-700 mb-1">Premios Pendientes:</p>
                {(pendingVentas.premio_macho || 0) > 0 && (
                  <p className="text-yellow-800">• PREMIO MACHO: ₡{(pendingVentas.premio_macho || 0).toFixed(2)}</p>
                )}
                {(pendingVentas.premio_tiro || 0) > 0 && (
                  <p className="text-yellow-800">• PREMIO TIRO: ₡{(pendingVentas.premio_tiro || 0).toFixed(2)}</p>
                )}
                {((pendingVentas.premio_macho || 0) + (pendingVentas.premio_tiro || 0)) > 0 && (
                  <p className="font-semibold text-yellow-900 mt-1">
                    Total: ₡{((pendingVentas.premio_macho || 0) + (pendingVentas.premio_tiro || 0)).toFixed(2)}
                  </p>
                )}
              </div>
            </div>
            <p className="text-xs text-yellow-700 mt-2 italic">
              Estos valores están incluidos en el cálculo de &quot;Efectivo Esperado&quot; pero aún no se han guardado. 
              Ve a Movimientos y presiona &quot;Guardar Ventas y Premios&quot; para registrarlos definitivamente.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Expected vs Actual Cash */}
      <Card className={`border-2 ${isMatch ? 'border-green-500 bg-green-50/50' : 'border-orange-500 bg-orange-50/50'}`}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            {isMatch ? (
              <CheckCircle className="w-4 h-4 text-green-600" />
            ) : (
              <AlertCircle className="w-4 h-4 text-orange-600" />
            )}
            Verificación de Efectivo
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Colones */}
            <div className="space-y-1">
              <p className="text-xs font-semibold text-slate-700">Colones (₡)</p>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-slate-600">Efectivo inicial:</span>
                  <span className="font-medium">{formatCurrency(cashRegister.initial_cash_colones)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Efectivo actual:</span>
                  <span className="font-bold text-blue-600">{formatCurrency(cashRegister.total_cash_colones)}</span>
                </div>
                <div className="flex justify-between border-t pt-1">
                  <span className="text-slate-600">Efectivo esperado:</span>
                  <span className="font-bold text-purple-600">{formatCurrency(expectedColones)}</span>
                </div>
                <div className={`flex justify-between font-bold ${Math.abs(diffColones) < 0.01 ? 'text-green-600' : 'text-orange-600'}`}>
                  <span>Diferencia:</span>
                  <span>{diffColones >= 0 ? '+' : ''}{formatCurrency(diffColones)}</span>
                </div>
              </div>
            </div>

            {/* Dollars */}
            <div className="space-y-1">
              <p className="text-xs font-semibold text-slate-700">Dólares ($)</p>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-slate-600">Efectivo inicial:</span>
                  <span className="font-medium">{formatCurrency(cashRegister.initial_cash_dollars, "dollars")}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Efectivo actual:</span>
                  <span className="font-bold text-blue-600">{formatCurrency(cashRegister.total_cash_dollars, "dollars")}</span>
                </div>
                <div className="flex justify-between border-t pt-1">
                  <span className="text-slate-600">Efectivo esperado:</span>
                  <span className="font-bold text-purple-600">{formatCurrency(expectedDollars, "dollars")}</span>
                </div>
                <div className={`flex justify-between font-bold ${Math.abs(diffDollars) < 0.01 ? 'text-green-600' : 'text-orange-600'}`}>
                  <span>Diferencia:</span>
                  <span>{diffDollars >= 0 ? '+' : ''}{formatCurrency(diffDollars, "dollars")}</span>
                </div>
              </div>
            </div>
          </div>

          {!isMatch && (
            <div className="mt-4 p-3 bg-orange-100 border border-orange-300 rounded-lg">
              <p className="text-sm text-orange-800">
                <AlertCircle className="w-4 h-4 inline mr-2" />
                <strong>Atención:</strong> El efectivo en caja no coincide con lo esperado según los movimientos registrados.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transaction Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {/* Ventas en Efectivo */}
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs flex items-center gap-2 text-green-700">
              <TrendingUp className="w-3 h-3" />
              Ventas en Efectivo
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <p className="text-xs text-slate-600 mb-1">{groups.ventas.count} transacciones</p>
            <p className="text-base font-bold text-green-700">{formatCurrency(groups.ventas.colones)}</p>
            <p className="text-xs font-semibold text-green-600">{formatCurrency(groups.ventas.dollars, "dollars")}</p>
            <p className="text-xs text-slate-500 mt-1">Suma a ventas y caja</p>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCreateNew("ventas")}
                className="text-xs h-7"
              >
                <Plus className="w-3 h-3 mr-1" />
                Nuevo
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleViewDetails("ventas")}
                className="text-xs h-7"
              >
                <Eye className="w-3 h-3 mr-1" />
                Ver
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* SINPE Móvil */}
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs flex items-center gap-2 text-blue-700">
              <DollarSign className="w-3 h-3" />
              SINPE Móvil
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <p className="text-xs text-slate-600 mb-1">{groups.sinpe.count} transacciones</p>
            <p className="text-base font-bold text-blue-700">{formatCurrency(groups.sinpe.colones)}</p>
            <p className="text-sm font-semibold text-blue-600">{formatCurrency(groups.sinpe.dollars, "dollars")}</p>
            <p className="text-xs text-slate-500 mt-1">Suma a ventas, NO a caja</p>
            {groups.tarjeta_banco.count > 0 && (
              <div className="mt-2 pt-2 border-t border-blue-200" data-testid="live-report-tarjeta-banco">
                <p className="text-xs font-semibold text-purple-700">Pago por Banco/Tarjeta ({groups.tarjeta_banco.count})</p>
                <p className="text-sm font-bold text-purple-700">{formatCurrency(groups.tarjeta_banco.colones)}</p>
                {groups.tarjeta_banco.dollars > 0 && <p className="text-xs font-semibold text-purple-600">{formatCurrency(groups.tarjeta_banco.dollars, "dollars")}</p>}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCreateNew("sinpe")}
                className="text-xs"
              >
                <Plus className="w-3 h-3 mr-1" />
                Nuevo
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleViewDetails("sinpe")}
                className="text-xs"
              >
                <Eye className="w-3 h-3 mr-1" />
                Ver
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Bancos */}
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2 text-purple-700">
              <DollarSign className="w-4 h-4" />
              Pagos por Banco
              {selectedBox?.uses_banks === false && (
                <span className="text-[10px] bg-orange-200 text-orange-700 px-1.5 py-0.5 rounded">Deshabilitado</span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-slate-600 mb-1">{groups.bancos.count} transacciones</p>
            <p className="text-lg font-bold text-purple-700">{formatCurrency(groups.bancos.colones)}</p>
            <p className="text-sm font-semibold text-purple-600">{formatCurrency(groups.bancos.dollars, "dollars")}</p>
            <p className="text-xs text-slate-500 mt-1">Suma a ventas y caja</p>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCreateNew("bancos")}
                className="text-xs"
                disabled={selectedBox?.uses_banks === false}
                title={selectedBox?.uses_banks === false ? "Esta caja no usa bancos - solo se puede crear desde Excel Import" : ""}
              >
                <Plus className="w-3 h-3 mr-1" />
                Nuevo
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleViewDetails("bancos")}
                className="text-xs"
              >
                <Eye className="w-3 h-3 mr-1" />
                Ver
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Pagos con Tarjeta HOY — desglose por banco/datáfono */}
        <Card className={`${cardIncomeTotal > 0 ? "bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-300 ring-2 ring-emerald-200" : "bg-slate-50 border-slate-200"}`} data-testid="card-income-today">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2 text-emerald-700">
              <CreditCard className="w-4 h-4" />
              Tarjeta (Datáfonos) Hoy
              {cardIncomeTotal > 0 && (
                <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold text-white bg-emerald-600 px-2 py-0.5 rounded-full">
                  <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                  ACTIVO
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {cardIncomeTotal === 0 ? (
              <p className="text-xs text-slate-500">Sin ingresos por tarjeta aún hoy.</p>
            ) : (
              <>
                <p className="text-lg font-bold text-emerald-700">{formatCurrency(cardIncomeTotal)}</p>
                <p className="text-xs text-slate-500 mt-0.5">Acreditado automáticamente a los bancos</p>
                <div className="mt-2 space-y-1 max-h-32 overflow-y-auto pr-1">
                  {cardIncomeByBank.map((row) => (
                    <div key={row.bank_id} className="flex items-center justify-between rounded bg-white/70 border border-emerald-200 px-2 py-1" data-testid={`card-bank-${row.bank_id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-emerald-900 truncate">🏦 {row.bank_name}</p>
                        <p className="text-[10px] text-slate-500 truncate">
                          {Object.entries(row.per_datafono).map(([n, a]) => `${n}: ${formatCurrency(a)}`).join(" · ")}
                        </p>
                      </div>
                      <span className="ml-2 text-xs font-bold text-emerald-700 shrink-0">{formatCurrency(row.total)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Premios */}
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2 text-red-700">
              <TrendingDown className="w-4 h-4" />
              Premios (Salidas)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-slate-600 mb-1">{groups.premios.count} transacciones</p>
            <p className="text-lg font-bold text-red-700">{formatCurrency(groups.premios.colones)}</p>
            <p className="text-sm font-semibold text-red-600">{formatCurrency(groups.premios.dollars, "dollars")}</p>
            <p className="text-xs text-slate-500 mt-1">Resta de caja</p>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCreateNew("premios")}
                className="text-xs"
              >
                <Plus className="w-3 h-3 mr-1" />
                Nuevo
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleViewDetails("premios")}
                className="text-xs"
              >
                <Eye className="w-3 h-3 mr-1" />
                Ver
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Entradas de Dinero */}
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2 text-emerald-700">
              <TrendingUp className="w-4 h-4" />
              Entradas de Dinero
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-slate-600 mb-1">{groups.entradas.count} transacciones</p>
            <p className="text-lg font-bold text-emerald-700">{formatCurrency(groups.entradas.colones)}</p>
            <p className="text-sm font-semibold text-emerald-600">{formatCurrency(groups.entradas.dollars, "dollars")}</p>
            <p className="text-xs text-slate-500 mt-1">Suma a caja, NO a ventas</p>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCreateNew("entradas")}
                className="text-xs"
              >
                <Plus className="w-3 h-3 mr-1" />
                Nuevo
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleViewDetails("entradas")}
                className="text-xs"
              >
                <Eye className="w-3 h-3 mr-1" />
                Ver
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Salidas de Dinero */}
        <Card className="bg-gradient-to-br from-rose-50 to-rose-100 border-rose-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2 text-rose-700">
              <TrendingDown className="w-4 h-4" />
              Salidas de Dinero
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-slate-600 mb-1">{groups.salidas.count} transacciones</p>
            <p className="text-lg font-bold text-rose-700">{formatCurrency(groups.salidas.colones)}</p>
            <p className="text-sm font-semibold text-rose-600">{formatCurrency(groups.salidas.dollars, "dollars")}</p>
            <p className="text-xs text-slate-500 mt-1">Resta de caja, NO de ventas</p>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCreateNew("salidas")}
                className="text-xs"
              >
                <Plus className="w-3 h-3 mr-1" />
                Nuevo
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleViewDetails("salidas")}
                className="text-xs"
              >
                <Eye className="w-3 h-3 mr-1" />
                Ver
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Salida Premios */}
        {/* Pago Días Anteriores (Afecta Caja) */}
        <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2 text-cyan-700">
              <TrendingUp className="w-4 h-4" />
              Pago Días Anteriores (Afecta)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-slate-600 mb-1">{groups.pago_dias_anteriores_afecta.count} transacciones</p>
            <p className="text-lg font-bold text-cyan-700">{formatCurrency(groups.pago_dias_anteriores_afecta.colones)}</p>
            <p className="text-sm font-semibold text-cyan-600">{formatCurrency(groups.pago_dias_anteriores_afecta.dollars, "dollars")}</p>
            <p className="text-xs text-slate-500 mt-1">Suma a caja, pago anterior</p>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCreateNew("pago_dias_anteriores_afecta")}
                className="text-xs"
              >
                <Plus className="w-3 h-3 mr-1" />
                Nuevo
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleViewDetails("pago_dias_anteriores_afecta")}
                className="text-xs"
              >
                <Eye className="w-3 h-3 mr-1" />
                Ver
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Pago Días Anteriores (NO Afecta Caja) */}
        <Card className="bg-gradient-to-br from-cyan-50 to-white border-cyan-100">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2 text-cyan-600">
              <TrendingUp className="w-4 h-4" />
              Pago Días Anteriores (NO Afecta)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-slate-600 mb-1">{groups.pago_dias_anteriores_no_afecta.count} transacciones</p>
            <p className="text-lg font-bold text-cyan-600">{formatCurrency(groups.pago_dias_anteriores_no_afecta.colones)}</p>
            <p className="text-sm font-semibold text-cyan-500">{formatCurrency(groups.pago_dias_anteriores_no_afecta.dollars, "dollars")}</p>
            <p className="text-xs text-slate-500 mt-1">NO afecta caja, solo registro</p>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCreateNew("pago_dias_anteriores_no_afecta")}
                className="text-xs"
              >
                <Plus className="w-3 h-3 mr-1" />
                Nuevo
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleViewDetails("pago_dias_anteriores_no_afecta")}
                className="text-xs"
              >
                <Eye className="w-3 h-3 mr-1" />
                Ver
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Pago en Efectivo - REMOVED */}

        {/* Pago con Premio (Afecta Caja) */}
        <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2 text-purple-600">
              <CheckCircle className="w-4 h-4" />
              Pago con Premio (Afecta)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-slate-600 mb-1">{groups.pago_con_premio_afecta.count} transacciones</p>
            <p className="text-lg font-bold text-purple-600">{formatCurrency(groups.pago_con_premio_afecta.colones)}</p>
            <p className="text-sm font-semibold text-purple-500">{formatCurrency(groups.pago_con_premio_afecta.dollars, "dollars")}</p>
            <p className="text-xs text-slate-500 mt-1">SÍ afecta caja, suma al efectivo</p>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCreateNew("pago_con_premio_afecta")}
                className="text-xs"
              >
                <Plus className="w-3 h-3 mr-1" />
                Nuevo
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleViewDetails("pago_con_premio_afecta")}
                className="text-xs"
              >
                <Eye className="w-3 h-3 mr-1" />
                Ver
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Cuentas por Cobrar */}
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs flex items-center gap-2 text-amber-700">
              <TrendingDown className="w-3 h-3" />
              Cuentas por Cobrar
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <p className="text-xs text-slate-600 mb-1">{groups.cuentas_por_cobrar.count} transacciones</p>
            <p className="text-base font-bold text-amber-700">{formatCurrency(groups.cuentas_por_cobrar.colones)}</p>
            <p className="text-xs font-semibold text-amber-600">{formatCurrency(groups.cuentas_por_cobrar.dollars, "dollars")}</p>
            <p className="text-xs text-slate-500 mt-1">Resta de caja (dinero prestado)</p>
            <div className="grid grid-cols-3 gap-1 mt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCreateNew("cuentas_por_cobrar")}
                className="text-xs h-7"
              >
                <Plus className="w-3 h-3 mr-1" />
                Nuevo
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/debts')}
                className="text-xs h-7 bg-green-50 hover:bg-green-100 border-green-300"
                title="Ir al módulo de Cuentas para pagar deudas"
              >
                💰 Pagar
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleViewDetails("cuentas_por_cobrar")}
                className="text-xs h-7"
              >
                <Eye className="w-3 h-3 mr-1" />
                Ver
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Cuentas por Pagar */}
        <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs flex items-center gap-2 text-indigo-700">
              <TrendingUp className="w-3 h-3" />
              Cuentas por Pagar
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <p className="text-xs text-slate-600 mb-1">{groups.cuentas_por_pagar.count} transacciones</p>
            <p className="text-base font-bold text-indigo-700">{formatCurrency(groups.cuentas_por_pagar.colones)}</p>
            <p className="text-xs font-semibold text-indigo-600">{formatCurrency(groups.cuentas_por_pagar.dollars, "dollars")}</p>
            <p className="text-xs text-slate-500 mt-1">Suma a caja (recibido a crédito)</p>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCreateNew("cuentas_por_pagar")}
                className="text-xs h-7"
              >
                <Plus className="w-3 h-3 mr-1" />
                Nuevo
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleViewDetails("cuentas_por_pagar")}
                className="text-xs h-7"
              >
                <Eye className="w-3 h-3 mr-1" />
                Ver
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Ventas de Inventario */}
        {(groups.ventas_inventario.count > 0 || groups.pago_electronico_inventario.count > 0 || groups.nota_credito_inventario.count > 0) && (
          <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200 lg:col-span-3">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-teal-700">
                <TrendingUp className="w-4 h-4" />
                Ventas de Inventario
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-2">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {/* Efectivo */}
                <div className="p-2 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-xs font-semibold text-green-700 mb-1">Efectivo</p>
                  <p className="text-xs text-slate-600">{groups.ventas_inventario.count} ventas</p>
                  <p className="text-base font-bold text-green-700">{formatCurrency(groups.ventas_inventario.colones)}</p>
                  <p className="text-xs text-green-600">SÍ afecta caja</p>
                </div>
                
                {/* SINPE / Tarjeta combinado */}
                <div className="p-2 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs font-semibold text-blue-700 mb-1">SINPE / Tarjeta</p>
                  <p className="text-xs text-slate-600">
                    {groups.pago_electronico_inventario.count} ventas 
                    {(groups.pago_electronico_inventario.sinpe > 0 || groups.pago_electronico_inventario.tarjeta > 0) && (
                      <span className="ml-1">
                        ({groups.pago_electronico_inventario.sinpe || 0} SINPE, {groups.pago_electronico_inventario.tarjeta || 0} Tarjeta)
                      </span>
                    )}
                  </p>
                  <p className="text-base font-bold text-blue-700">{formatCurrency(groups.pago_electronico_inventario.colones)}</p>
                  <p className="text-xs text-blue-600">NO afecta caja</p>
                </div>
                
                {/* Notas de Crédito (Devoluciones) */}
                <div className="p-2 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-xs font-semibold text-red-700 mb-1">Notas Crédito</p>
                  <p className="text-xs text-slate-600">{groups.nota_credito_inventario.count} anulaciones</p>
                  <p className="text-base font-bold text-red-700">-{formatCurrency(groups.nota_credito_inventario.colones)}</p>
                  <p className="text-xs text-red-600">Resta de caja</p>
                </div>
              </div>
              
              <div className="mt-3 pt-2 border-t border-teal-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-teal-700">Total Ventas Inventario:</span>
                  <span className="text-lg font-bold text-teal-800">
                    {formatCurrency(
                      groups.ventas_inventario.colones + 
                      groups.pago_electronico_inventario.colones - 
                      groups.nota_credito_inventario.colones
                    )}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Efectivo en caja: {formatCurrency(groups.ventas_inventario.colones - groups.nota_credito_inventario.colones)}
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Total Sales */}
      <Card className="bg-gradient-to-r from-slate-700 to-slate-800 text-white">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Ventas Totales del Día</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm opacity-90">Colones</p>
              <p className="text-3xl font-bold">{formatCurrency(cashRegister.total_sales_colones)}</p>
            </div>
            <div>
              <p className="text-sm opacity-90">Dólares</p>
              <p className="text-3xl font-bold">{formatCurrency(cashRegister.total_sales_dollars, "dollars")}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transaction Details Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <List className="w-5 h-5" />
              Movimientos - {selectedType ? getTypeLabel(selectedType === "bancos" ? "banco_" : selectedType) : ""}
            </DialogTitle>
            <DialogDescription>
              Detalle de todas las transacciones de este tipo
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {selectedType && getTransactionsByType(selectedType).length > 0 ? (
              <div className="space-y-3">
                {getTransactionsByType(selectedType).map((transaction, index) => {
                  const isVoided = transaction.voided === true;
                  return (
                  <Card key={index} className={`border-l-4 ${isVoided ? "border-l-red-500 bg-red-50/50 opacity-70" : "border-l-blue-500"}`} data-testid={`livereport-tx-${transaction.id}`}>
                    <CardContent className="pt-4">
                      {isVoided && (
                        <div className="mb-2 flex items-center gap-2">
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-red-600 text-white" data-testid={`livereport-voided-badge-${transaction.id}`}>
                            Eliminado
                          </span>
                          {transaction.voided_at && (
                            <span className="text-[11px] text-red-700">
                              Anulado {formatDateTime(transaction.voided_at)}{transaction.voided_by ? ` por ${transaction.voided_by}` : ""}
                            </span>
                          )}
                        </div>
                      )}
                      <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${isVoided ? "line-through decoration-red-500 decoration-2" : ""}`}>
                        <div>
                          <p className="text-sm font-semibold text-slate-700 no-underline">Tipo</p>
                          <p className="text-sm text-slate-600">{transaction.channel === "tarjeta" ? "Pago por Banco/Tarjeta" : getTypeLabel(transaction.type)}</p>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-700 no-underline">Fecha y Hora</p>
                          <p className="text-sm text-slate-600">{formatDateTime(transaction.created_at)}</p>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-700 no-underline">Montos</p>
                          <p className="text-sm text-slate-600">
                            ₡{transaction.amount_colones.toFixed(2)} / ${transaction.amount_dollars.toFixed(2)}
                          </p>
                        </div>
                      </div>
                      {transaction.description && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-sm font-semibold text-slate-700">Descripción</p>
                          <p className={`text-sm text-slate-600 ${isVoided ? "line-through decoration-red-500" : ""}`}>{transaction.description}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-slate-500">No hay transacciones de este tipo</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Transaction Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              {selectedTypeForCreate === "cuentas_por_cobrar" 
                ? "Nueva Cuenta por Cobrar" 
                : selectedTypeForCreate === "cuentas_por_pagar"
                ? "Nueva Cuenta por Pagar"
                : `Nuevo Movimiento - ${selectedTypeForCreate ? getTypeLabel(selectedTypeForCreate === "bancos" ? "banco_" : selectedTypeForCreate) : ""}`
              }
            </DialogTitle>
            <DialogDescription>
              {selectedTypeForCreate === "cuentas_por_cobrar" || selectedTypeForCreate === "cuentas_por_pagar"
                ? "Ingresa los datos de la cuenta"
                : "Ingresa los datos del movimiento rápidamente"
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Customer Name (only for cuentas_por_cobrar and cuentas_por_pagar) */}
            {(selectedTypeForCreate === "cuentas_por_cobrar" || selectedTypeForCreate === "cuentas_por_pagar") && (
              <div className="space-y-2">
                <Label htmlFor="customer_name">
                  {selectedTypeForCreate === "cuentas_por_cobrar" ? "Nombre del Cliente *" : "Nombre del Proveedor"}
                </Label>
                <Input
                  id="customer_name"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  placeholder={selectedTypeForCreate === "cuentas_por_cobrar" ? "Ej: Juan Pérez" : "Ej: Proveedor ABC"}
                />
              </div>
            )}

            {/* Bank Selector (only for bancos) */}
            {selectedTypeForCreate === "bancos" && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Tipo de Movimiento *</Label>
                  <Select value={bankMovementType} onValueChange={setBankMovementType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="entrada">📥 Entrada (Depósito al banco)</SelectItem>
                      <SelectItem value="salida">📤 Salida (Pago desde banco)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {bankMovementType === "entrada" 
                      ? "Dinero que entra al banco (ej: cliente deposita)"
                      : "Dinero que sale del banco (ej: pago a proveedor)"
                    }
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Banco/Bóveda *</Label>
                  <Select value={selectedBank} onValueChange={setSelectedBank}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar banco" />
                    </SelectTrigger>
                    <SelectContent>
                      {banks.map((bank) => (
                        <SelectItem key={bank.id} value={bank.id}>
                          {bank.name} - ₡{(bank.total_colones || 0).toLocaleString('es-CR', {minimumFractionDigits: 2})}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* Mostrar saldo del banco seleccionado para salidas */}
                  {bankMovementType === "salida" && selectedBank && (
                    <div className="p-2 bg-blue-50 rounded border border-blue-200">
                      <p className="text-sm text-blue-800">
                        💰 Saldo disponible: 
                        <span className="font-bold ml-1">
                          ₡{(banks.find(b => b.id === selectedBank)?.total_colones || 0).toLocaleString('es-CR', {minimumFractionDigits: 2})}
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Vault/Bank Selector (only for SINPE) */}
            {selectedTypeForCreate === "sinpe" && (
              <div className="space-y-2">
                <Label>Banco (Bóveda) de Destino *</Label>
                {!showNewVaultInput ? (
                  <div className="space-y-2">
                    <Select 
                      value={formData.vault_name} 
                      onValueChange={(value) => {
                        if (value === "_new_") {
                          setShowNewVaultInput(true);
                          setFormData({ ...formData, vault_name: "" });
                        } else {
                          setFormData({ ...formData, vault_name: value });
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar banco..." />
                      </SelectTrigger>
                      <SelectContent>
                        {vaults.map((vault) => (
                          <SelectItem key={vault.id} value={vault.name}>
                            {vault.name}
                          </SelectItem>
                        ))}
                        <SelectItem value="_new_">+ Crear nueva bóveda</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      value={newVaultName}
                      onChange={(e) => setNewVaultName(e.target.value)}
                      placeholder="Nombre de la nueva bóveda"
                    />
                    <Button
                      type="button"
                      size="sm"
                      onClick={async () => {
                        if (!newVaultName.trim()) return;
                        try {
                          await axiosInstance.post('/banks', {
                            name: newVaultName,
                            description: ""
                          });
                          // Refresh banks
                          const response = await axiosInstance.get('/banks');
                          setVaults(response.data);
                          setFormData({ ...formData, vault_name: newVaultName });
                          setShowNewVaultInput(false);
                          setNewVaultName("");
                          toast.success("Banco creado");
                        } catch (error) {
                          toast.error("Error al crear banco");
                        }
                      }}
                    >
                      ✓
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setShowNewVaultInput(false);
                        setNewVaultName("");
                      }}
                    >
                      ✕
                    </Button>
                  </div>
                )}
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="amount_colones">Monto en Colones (₡)</Label>
              <Input
                id="amount_colones"
                type="number"
                step="0.01"
                min="0"
                value={formData.amount_colones}
                onChange={(e) => setFormData({ ...formData, amount_colones: e.target.value })}
                placeholder="0.00"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="amount_dollars">Monto en Dólares ($)</Label>
              <Input
                id="amount_dollars"
                type="number"
                step="0.01"
                min="0"
                value={formData.amount_dollars}
                onChange={(e) => setFormData({ ...formData, amount_dollars: e.target.value })}
                placeholder="0.00"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Descripción {selectedTypeForCreate === "cuentas_por_cobrar" || selectedTypeForCreate === "cuentas_por_pagar" ? "(opcional)" : ""}</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descripción..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateTransaction} disabled={creating}>
              {creating ? "Guardando..." : selectedTypeForCreate === "cuentas_por_cobrar" || selectedTypeForCreate === "cuentas_por_pagar" ? "Crear Cuenta" : "Guardar Movimiento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}